/**
 * A change the memo chat proposes (DOP-618, DOP-653): a `memo-edit` block in
 * its answer, read, matched to the part of the memo it changes, and put into
 * the edit draft when the person applies it.
 *
 * The chat never writes the memo. Applying puts the change into the draft
 * kept on this computer and opens edit mode with it in place, marked as a
 * change; nothing reaches the memo until the person saves a new version, the
 * same as their own typing.
 *
 * A proposal is one of:
 *  - a paragraph reworded, added or taken out (the first kind there was, and
 *    what a block with no `kind` is);
 *  - a table inside a section, given whole as it should read;
 *  - values for the financial summary, with the rows and columns they need;
 *  - values for the company facts;
 *  - a section's title.
 *
 * Whatever a proposal adds that states a fact names the document it came
 * from (DOP-652). That place rides into the draft on the words, the cell or
 * the value, and the server re-reads it when the version is saved.
 *
 * The chat names a paragraph by how it reads now (`replace`), since the
 * generated anchor is not something it sees; a table by its caption; a figure
 * by its key or its label. Each is matched here by wording. Close is enough,
 * and no match means the change cannot be placed, which the card says.
 */

import type { JSONContent } from "@tiptap/react";
import { z } from "zod/v3";

import type {
	DealScreeningChart,
	DealScreeningCustomFigure,
	DealScreeningFact,
	DealScreeningRunFigure,
	DealScreeningSection,
	DealScreeningSource,
} from "../../types/dealScreening";
import { formatFactValue, formatTableValue } from "./dealScreeningFormat";
import {
	type DocumentSource,
	type KnownSources,
	documentSourceLabel,
	fromProposalSource,
	sourceKey,
} from "./documentSource";
import {
	type FiguresDraft,
	type FiguresGrid,
	type SavedTableOrder,
	columnName,
	figuresGrid,
	headerText,
	samePeriod,
	typedFigure,
	withEntry,
	withHeader,
	withNewColumn,
	withColumnName,
	withRow,
} from "./figuresEditModel";
import { diffText } from "./memoDiff";
import { itemsFromDoc, sourceMark } from "./memoEditorModel";
import { type EditItem, itemsOf } from "./sectionEditModel";
import { type EditTable, cellKey, editTableOf, tableKey } from "./tableEditModel";

// ------------------------------------------------------------------ what the chat writes

const Source = z.unknown().optional();

const Sentence = z.union([
	z.string(),
	z.object({ text: z.string(), source: Source }).passthrough(),
]);

const Cell = z.union([
	z.string(),
	z.number(),
	z.null(),
	z.object({ text: z.union([z.string(), z.number()]).nullable().optional(), source: Source }).passthrough(),
]);

const ParagraphBlock = z.object({
	kind: z.literal("paragraph").optional(),
	section: z.string().min(1),
	replace: z.string().nullable().optional(),
	lead: z.string().nullable().optional(),
	sentences: z.array(Sentence).optional(),
	delete: z.boolean().optional(),
});

const TableBlock = z.object({
	kind: z.literal("table"),
	section: z.string().min(1),
	replace: z.string().nullable().optional(),
	caption: z.string().nullable().optional(),
	columns: z.array(z.union([z.string(), z.number()])).max(12).optional(),
	rows: z.array(z.array(Cell).max(12)).min(1).max(60),
});

const SectionBlock = z.object({
	kind: z.literal("section"),
	title: z.string().min(1),
	after: z.string().nullable().optional(),
	paragraphs: z
		.array(
			z.object({
				lead: z.string().nullable().optional(),
				sentences: z.array(Sentence).min(1),
			}),
		)
		.min(1)
		.max(20),
});

const CHART_TYPES = ["bar", "line", "area", "pie"] as const;
const VALUE_FORMATS = ["currency_usd", "percentage", "ratio", "bps", "number", "compact"] as const;
const ChartNumber = z.union([z.number(), z.string(), z.null()]);

const ChartBlock = z.object({
	kind: z.literal("chart"),
	section: z.string().min(1),
	replace: z.string().nullable().optional(),
	chart_type: z.enum(CHART_TYPES),
	title: z.string().nullable().optional(),
	data: z
		.object({
			labels: z.array(z.union([z.string(), z.number()])).max(60).optional(),
			series: z
				.array(
					z.object({
						name: z.string().optional(),
						values: z.array(ChartNumber).max(60),
						value_format: z.enum(VALUE_FORMATS).optional(),
					}),
				)
				.max(8)
				.optional(),
			segments: z.array(z.object({ name: z.string(), value: ChartNumber })).max(60).optional(),
			value_format: z.enum(VALUE_FORMATS).optional(),
		})
		.passthrough(),
	sources: z.array(Source).max(20).optional(),
});

const UNITS = ["usd_m", "pct", "x", "count", "per_share"] as const;

const FiguresBlock = z.object({
	kind: z.literal("figures"),
	columns: z.array(z.string()).max(12).optional(),
	rows: z
		.array(
			z.object({
				label: z.string().min(1),
				unit: z.enum(UNITS).optional(),
				periods: z.enum(["by_year", "point"]).optional(),
			}),
		)
		.max(20)
		.optional(),
	values: z
		.array(
			z
				.object({
					figure: z.string().min(1),
					period: z.string().nullable().optional(),
					value: z.union([z.number(), z.string()]),
					source: Source,
					note: z.string().optional(),
				})
				.passthrough(),
		)
		.min(1)
		.max(200),
});

const FactsBlock = z.object({
	kind: z.literal("company_facts"),
	values: z
		.array(
			z
				.object({
					field: z.string().min(1),
					value: z.union([z.number(), z.string()]),
					source: Source,
					note: z.string().optional(),
				})
				.passthrough(),
		)
		.min(1)
		.max(40),
});

const TitleBlock = z.object({
	kind: z.literal("title"),
	section: z.string().min(1),
	title: z.string().min(1).max(200),
});

// ------------------------------------------------------------------ what it means

/** Words of a proposal that name the document they came from. */
export interface ProposalSource {
	/** The words, as they read in the proposal. */
	quote: string;
	source: DocumentSource;
}

/** A paragraph reworded, added (`replace` null) or taken out (`delete`). */
export interface MemoEditProposal {
	kind?: "paragraph";
	section: string;
	replace?: string | null;
	lead?: string | null;
	sentences: string[];
	sources?: ProposalSource[];
	delete?: boolean;
}

/** A table inside a section, whole, as it should read. */
export interface TableProposal {
	kind: "table";
	section: string;
	replace: string | null;
	table: EditTable;
}

/** A section the memo does not have (DOP-656): its heading, place and paragraphs. */
export interface SectionProposal {
	kind: "section";
	title: string;
	/** The key of the section it follows; null for the end of the memo. */
	after: string | null;
	paragraphs: { lead: string | null; sentences: string[]; sources: ProposalSource[] }[];
}

/** A chart inside a section (DOP-655), whole, as it should be drawn. */
export interface ChartProposal {
	kind: "chart";
	section: string;
	/** The title of the chart it replaces; null for a new one at the end. */
	replace: string | null;
	chart: DealScreeningChart;
	sources: DocumentSource[];
}

export interface FigureValue {
	figure: string;
	period: string | null;
	value: number;
	source: DocumentSource | null;
	note: string;
}

export interface FiguresProposal {
	kind: "figures";
	columns: string[];
	rows: { label: string; unit: DealScreeningCustomFigure["unit"]; periods: "by_year" | "point" }[];
	values: FigureValue[];
}

export interface FactsProposal {
	kind: "company_facts";
	values: { field: string; value: string | number; source: DocumentSource | null; note: string }[];
}

export interface TitleProposal {
	kind: "title";
	section: string;
	title: string;
}

export type MemoEdit =
	| MemoEditProposal
	| TableProposal
	| FiguresProposal
	| FactsProposal
	| TitleProposal
	| ChartProposal
	| SectionProposal;

type FilenameOf = KnownSources["filenameOf"] | KnownSources;

const NO_NAMES: FilenameOf = () => null;

const clean = (text: unknown): string => String(text ?? "").replace(/\s+/g, " ").trim();

const sameName = (a: string, b: string): boolean => clean(a).toLowerCase() === clean(b).toLowerCase();

/**
 * A number as the chat wrote it. It is told to write numbers; a number
 * written as text ("14.1") is still one, and anything else is not a value.
 */
const numberOf = (value: number | string): number | null => {
	if (typeof value === "number") return Number.isFinite(value) ? value : null;
	const text = value.trim().replace(/,/g, "");
	if (!/^-?\d*\.?\d+$/.test(text)) return null;
	return Number(text);
};

/**
 * The block's JSON as a proposal, or null while it is still streaming or is
 * not one.
 *
 * The chat escapes a `$` before a digit so currency is not read as math
 * (`normalizeMathDelimiters`), and that reaches fenced code too: "$2.25mm"
 * arrives as "\$2.25mm", which is not valid JSON. It is put back first.
 */
export function parseEdit(raw: string, filenameOf: FilenameOf = NO_NAMES): MemoEdit | null {
	let data: unknown;
	try {
		data = JSON.parse(raw.replace(/\\\$/g, "$"));
	} catch {
		return null;
	}
	if (!data || typeof data !== "object") return null;
	const kind = (data as { kind?: unknown }).kind;
	const source = (value: unknown) => fromProposalSource(value, filenameOf);

	if (kind === "table") {
		const parsed = TableBlock.safeParse(data);
		if (!parsed.success) return null;
		const sources: Record<string, DocumentSource> = {};
		const rows = parsed.data.rows.map((row, r) =>
			row.map((cell, c) => {
				if (cell === null || typeof cell !== "object") return clean(cell);
				const found = source(cell.source);
				const text = clean(cell.text);
				if (found && text) sources[cellKey(r, c)] = found;
				return text;
			}),
		);
		if (!rows.some((row) => row.some(Boolean))) return null;
		return {
			kind: "table",
			section: parsed.data.section,
			replace: clean(parsed.data.replace) || null,
			table: {
				caption: clean(parsed.data.caption) || null,
				columns: (parsed.data.columns ?? []).map(clean),
				rows,
				...(Object.keys(sources).length > 0 ? { sources } : {}),
			},
		};
	}
	if (kind === "figures") {
		const parsed = FiguresBlock.safeParse(data);
		if (!parsed.success) return null;
		const values = parsed.data.values.flatMap((entry): FigureValue[] => {
			const value = numberOf(entry.value);
			if (value === null) return [];
			return [
				{
					figure: clean(entry.figure),
					period: clean(entry.period) || null,
					value,
					source: source(entry.source),
					note: clean(entry.note),
				},
			];
		});
		if (values.length === 0) return null;
		return {
			kind: "figures",
			columns: (parsed.data.columns ?? []).map(clean).filter(Boolean),
			rows: (parsed.data.rows ?? []).map((row) => ({
				label: clean(row.label),
				unit: row.unit ?? "usd_m",
				periods: row.periods ?? "by_year",
			})),
			values,
		};
	}
	if (kind === "company_facts") {
		const parsed = FactsBlock.safeParse(data);
		if (!parsed.success) return null;
		return {
			kind: "company_facts",
			values: parsed.data.values.map((entry) => ({
				field: clean(entry.field),
				value: typeof entry.value === "number" ? entry.value : clean(entry.value),
				source: source(entry.source),
				note: clean(entry.note),
			})),
		};
	}
	if (kind === "section") {
		const parsed = SectionBlock.safeParse(data);
		if (!parsed.success) return null;
		const paragraphs = parsed.data.paragraphs.flatMap((paragraph) => {
			const sources: ProposalSource[] = [];
			const sentences = paragraph.sentences.flatMap((sentence) => {
				const text = clean(typeof sentence === "string" ? sentence : sentence.text);
				if (!text) return [];
				const found = typeof sentence === "string" ? null : source(sentence.source);
				if (found) sources.push({ quote: text, source: found });
				return [text];
			});
			return sentences.length > 0 ? [{ lead: clean(paragraph.lead) || null, sentences, sources }] : [];
		});
		if (paragraphs.length === 0) return null;
		return {
			kind: "section",
			title: clean(parsed.data.title),
			after: clean(parsed.data.after) || null,
			paragraphs,
		};
	}
	if (kind === "chart") {
		const parsed = ChartBlock.safeParse(data);
		if (!parsed.success) return null;
		const found = parsed.data;
		const value = (v: number | string | null) => (v === null ? null : numberOf(v));
		let chartData: Record<string, unknown>;
		if (found.chart_type === "pie") {
			const segments = (found.data.segments ?? []).flatMap((segment) => {
				const n = value(segment.value);
				const name = clean(segment.name);
				return n === null || !name ? [] : [{ name, value: n }];
			});
			if (segments.length === 0) return null;
			chartData = { segments };
		} else {
			const labels = (found.data.labels ?? []).map(clean);
			const series = (found.data.series ?? []).flatMap((entry) => {
				const values = entry.values.map(value);
				if (values.length !== labels.length || values.every((v) => v === null)) return [];
				return [
					{
						name: clean(entry.name),
						values,
						...(entry.value_format ? { value_format: entry.value_format } : {}),
					},
				];
			});
			if (labels.length === 0 || series.length === 0) return null;
			chartData = { labels, series };
		}
		if (found.data.value_format) chartData.value_format = found.data.value_format;
		return {
			kind: "chart",
			section: found.section,
			replace: clean(found.replace) || null,
			chart: { chart_type: found.chart_type, title: clean(found.title) || null, data: chartData },
			sources: (found.sources ?? []).flatMap((entry) => {
				const read = source(entry);
				return read ? [read] : [];
			}),
		};
	}
	if (kind === "title") {
		const parsed = TitleBlock.safeParse(data);
		return parsed.success
			? { kind: "title", section: parsed.data.section, title: clean(parsed.data.title) }
			: null;
	}
	if (kind !== undefined && kind !== "paragraph") return null;

	const parsed = ParagraphBlock.safeParse(data);
	if (!parsed.success) return null;
	const sources: ProposalSource[] = [];
	const sentences = (parsed.data.sentences ?? []).flatMap((sentence) => {
		const text = clean(typeof sentence === "string" ? sentence : sentence.text);
		if (!text) return [];
		const found = typeof sentence === "string" ? null : source(sentence.source);
		if (found) sources.push({ quote: text, source: found });
		return [text];
	});
	const remove = parsed.data.delete === true;
	if (remove ? !clean(parsed.data.replace) : sentences.length === 0) return null;
	return {
		section: parsed.data.section,
		replace: parsed.data.replace,
		lead: parsed.data.lead,
		sentences,
		...(sources.length > 0 ? { sources } : {}),
		...(remove ? { delete: true } : {}),
	};
}

/**
 * An answer's proposals, each able to stand on its own (DOP-653). The chat
 * may name a new row in one block and give more values for it in the next;
 * applied alone, the second would name a row the memo does not have. Every
 * figures proposal is given the rows its answer named, and adds the ones its
 * own values use.
 */
export function parseEdits(blocks: string[], filenameOf: FilenameOf = NO_NAMES): (MemoEdit | null)[] {
	const edits = blocks.map((block) => parseEdit(block, filenameOf));
	const rows: FiguresProposal["rows"] = [];
	edits.forEach((edit) => {
		if (edit?.kind !== "figures") return;
		edit.rows.forEach((row) => {
			if (!rows.some((known) => sameName(known.label, row.label))) rows.push(row);
		});
	});
	return edits.map((edit) => (edit?.kind === "figures" ? { ...edit, rows } : edit));
}

/** The block as a paragraph proposal, or null when it is another kind or not one. */
export function parseProposal(raw: string, filenameOf: FilenameOf = NO_NAMES): MemoEditProposal | null {
	const edit = parseEdit(raw, filenameOf);
	return edit && (edit.kind === undefined || edit.kind === "paragraph") ? edit : null;
}

// ------------------------------------------------------------------ finding what it changes

/** Wording only: no `[3]` marks, no markdown emphasis, one space. */
const normalize = (text: string): string =>
	text
		.replace(/\[\d+\]/g, "")
		.replace(/[*_`]/g, "")
		.replace(/\s+/g, " ")
		.trim()
		.toLowerCase();

const likeness = (a: string, b: string): number => {
	const parts = diffText(normalize(a), normalize(b));
	const same = parts.filter((p) => p.kind === "same").reduce((n, p) => n + p.text.length, 0);
	return same / Math.max(normalize(a).length, normalize(b).length, 1);
};

const itemText = (item: EditItem): string => [item.lead, item.text].filter(Boolean).join(" ");

/** How close wording must be to count as the paragraph the chat meant. */
export const MATCH_AT_LEAST = 0.6;

/** The index of the paragraph `replace` names in these items, or -1. */
export function matchParagraph(items: EditItem[], replace: string): number {
	let best = -1;
	let score = 0;
	items.forEach((item, index) => {
		if (item.table) return;
		const s = likeness(itemText(item), replace);
		if (s > score) {
			score = s;
			best = index;
		}
	});
	return score >= MATCH_AT_LEAST ? best : -1;
}

/**
 * The index of the table a caption names among these captions, or -1. A
 * section with one table is that table whatever the chat called it: a
 * caption is a label, and a near miss on the only table there is still it.
 */
export function matchTable(captions: (string | null)[], replace: string): number {
	let best = -1;
	let score = 0;
	captions.forEach((caption, index) => {
		const s = likeness(caption ?? "", replace);
		if (s > score) {
			score = s;
			best = index;
		}
	});
	if (score >= MATCH_AT_LEAST) return best;
	return captions.length === 1 ? 0 : -1;
}

// ------------------------------------------------------------------ the proposal, placed

/** What the card shows of one source: its name, and the link that opens it. */
export interface PlacedSource {
	label: string;
	source: DocumentSource;
}

interface PlacedBase {
	/** The card's heading: the section's title, or the block's name. */
	title: string;
	/** What changes, in a few words: "1 paragraph reworded". */
	summary: string;
	/** Why it cannot be applied, in words; null when it can. */
	problem: string | null;
	/** The documents the change names, each once. */
	sources: PlacedSource[];
}

export interface PlacedProposal extends PlacedBase {
	kind?: "paragraph";
	proposal: MemoEditProposal;
	section: DealScreeningSection;
	/** The paragraph it rewrites as the reader sees it, or null for a new one. */
	before: string | null;
	after: string;
}

export interface PlacedTable extends PlacedBase {
	kind: "table";
	proposal: TableProposal;
	section: DealScreeningSection;
	/** The table as it reads now, or null for a new one. */
	before: EditTable | null;
	after: EditTable;
	/** The cells of `after` that read differently, by "row:column". */
	changed: Set<string>;
}

export interface PlacedValue {
	label: string;
	/** The column it sits under, as the reader names it; "" for one latest value. */
	column: string;
	before: string;
	after: string;
	source: DocumentSource | null;
	note: string;
	/** Why this one value cannot be put in; the rest still can. */
	problem: string | null;
}

export interface PlacedFigures extends PlacedBase {
	kind: "figures";
	proposal: FiguresProposal;
	values: PlacedValue[];
}

export interface PlacedFacts extends PlacedBase {
	kind: "company_facts";
	proposal: FactsProposal;
	values: PlacedValue[];
}

export interface PlacedTitle extends PlacedBase {
	kind: "title";
	proposal: TitleProposal;
	section: DealScreeningSection;
	before: string;
	after: string;
}

export interface PlacedChart extends PlacedBase {
	kind: "chart";
	proposal: ChartProposal;
	section: DealScreeningSection;
	/** The chart it replaces, as it reads now, or null for a new one. */
	before: DealScreeningChart | null;
	after: DealScreeningChart;
}

export interface PlacedSection extends PlacedBase {
	kind: "section";
	proposal: SectionProposal;
	/** The section it goes after, or null for the end of the memo. */
	afterSection: DealScreeningSection | null;
}

export type PlacedEdit =
	| PlacedProposal
	| PlacedTable
	| PlacedFigures
	| PlacedFacts
	| PlacedTitle
	| PlacedChart
	| PlacedSection;

/** What a proposal is placed against: the memo as edit mode opens it. */
export interface ProposalContext {
	sections: DealScreeningSection[];
	facts: DealScreeningFact[];
	figures: DealScreeningRunFigure[] | undefined;
	/** The financial summary's saved order (DOP-649). */
	order?: SavedTableOrder;
	isEditable: (section: DealScreeningSection) => boolean;
	/** The heading of the company facts block, as the memo has it. */
	companyFactsTitle?: string;
}

const NOT_EDITABLE = "This section is built from the figures and cannot be reworded here.";

const distinct = (sources: (DocumentSource | null | undefined)[]): PlacedSource[] => {
	const seen = new Map<string, PlacedSource>();
	sources.forEach((source) => {
		if (source && !seen.has(sourceKey(source))) {
			seen.set(sourceKey(source), { label: documentSourceLabel(source), source });
		}
	});
	return Array.from(seen.values());
};

const count = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

function placeParagraph(
	sections: DealScreeningSection[],
	proposal: MemoEditProposal,
	isEditable: (section: DealScreeningSection) => boolean,
): PlacedProposal | null {
	const section = sections.find((s) => s.key === proposal.section);
	if (!section) return null;
	const after = [proposal.lead, ...proposal.sentences].filter(Boolean).join(" ");
	const base = {
		proposal,
		section,
		after,
		title: section.title,
		sources: distinct((proposal.sources ?? []).map((s) => s.source)),
	};
	const summary = proposal.delete
		? "1 paragraph taken out"
		: proposal.replace
			? "1 paragraph reworded"
			: "1 paragraph added";
	if (!isEditable(section)) return { ...base, summary, before: null, problem: NOT_EDITABLE };
	if (!proposal.replace) return { ...base, summary, before: null, problem: null };
	const items = itemsOf(section);
	const index = matchParagraph(items, proposal.replace);
	if (index < 0) {
		return {
			...base,
			summary,
			before: proposal.replace,
			problem: "Could not find this paragraph in the memo as it reads now.",
		};
	}
	return { ...base, summary, before: itemText(items[index]), problem: null };
}

/** The proposal against the memo on screen: what it replaces, and whether it can. */
export function placeProposal(
	sections: DealScreeningSection[],
	proposal: MemoEditProposal,
	isEditable: (section: DealScreeningSection) => boolean,
): PlacedProposal | null {
	return placeParagraph(sections, proposal, isEditable);
}

/** The cells of `after` that read differently from `before`, by place. */
export function changedCells(before: EditTable | null, after: EditTable): Set<string> {
	const changed = new Set<string>();
	after.rows.forEach((row, r) =>
		row.forEach((text, c) => {
			if (clean(before?.rows[r]?.[c]) !== clean(text)) changed.add(cellKey(r, c));
		}),
	);
	return changed;
}

function placeTable(context: ProposalContext, proposal: TableProposal): PlacedTable | null {
	const section = context.sections.find((s) => s.key === proposal.section);
	if (!section) return null;
	const tables = section.paragraphs.filter((p) => p.kind === "table" && p.table);
	const base = {
		kind: "table" as const,
		proposal,
		section,
		after: proposal.table,
		title: section.title,
		sources: distinct(Object.values(proposal.table.sources ?? {})),
	};
	const name = proposal.table.caption ? `the ${proposal.table.caption} table` : "a table";
	if (!context.isEditable(section)) {
		return {
			...base,
			before: null,
			changed: new Set(),
			summary: name,
			problem:
				section.key === "financial_summary"
					? "The financial summary is changed through its figures, not as a table."
					: NOT_EDITABLE,
		};
	}
	if (!proposal.replace) {
		return {
			...base,
			before: null,
			changed: changedCells(null, proposal.table),
			summary: `${name} added`,
			problem: null,
		};
	}
	const index = matchTable(
		tables.map((p) => p.table?.caption ?? null),
		proposal.replace,
	);
	if (index < 0) {
		return {
			...base,
			before: null,
			changed: new Set(),
			summary: name,
			problem: "Could not find this table in the memo as it reads now.",
		};
	}
	const before = editTableOf(tables[index].table);
	const changed = changedCells(before, proposal.table);
	const columns = proposal.table.columns.length - (before?.columns.length ?? 0);
	const rows = proposal.table.rows.length - (before?.rows.length ?? 0);
	const parts = [
		columns > 0 ? `${count(columns, "column")} added` : null,
		rows > 0 ? `${count(rows, "row")} added` : null,
		columns <= 0 && rows <= 0 && changed.size > 0 ? `${count(changed.size, "cell")} changed` : null,
	].filter(Boolean);
	return {
		...base,
		before,
		changed,
		summary: `${name}: ${parts.join(", ") || "as it reads now"}`,
		problem: tableKey(before) === tableKey(proposal.table) ? "The table already reads this way." : null,
	};
}

/** A row the summary shows, or one the settings list, that a name means. */
function findRow(grid: FiguresGrid, draft: FiguresDraft, name: string) {
	const rows = [...grid.series, ...grid.point];
	const shown = rows.find((row) => row.key === name) ?? rows.find((row) => sameName(row.label, name));
	if (shown) {
		return {
			key: shown.key,
			label: shown.label,
			unit: shown.unit,
			point: shown.point,
			derived: shown.derived,
			listed: false,
		};
	}
	const listed = grid.addable.find((f) => f.key === name) ?? grid.addable.find((f) => sameName(f.label, name));
	if (listed) {
		return {
			key: listed.key,
			label: listed.label,
			unit: listed.unit,
			point: listed.periods === "point",
			derived: false,
			listed: true,
		};
	}
	const added = draft.custom.find((f) => f.key === name) ?? draft.custom.find((f) => sameName(f.label, name));
	return added
		? {
				key: added.key,
				label: added.label,
				unit: added.unit,
				point: added.periods === "point",
				derived: false,
				listed: false,
			}
		: null;
}

/** The column a name means among the columns on screen, or null. */
const findColumn = (grid: FiguresGrid, draft: FiguresDraft, name: string): string | null =>
	grid.periods.find((p) => samePeriod(columnName(draft, p), name) || sameName(columnName(draft, p), name)) ?? null;

interface FiguresResult {
	draft: FiguresDraft;
	values: PlacedValue[];
}

/**
 * The financial summary's draft with the proposal in it, and each value as
 * the card lists it. Rows and columns the values need are added as a person
 * would add them; a value that cannot be put in says why and is left out.
 */
export function figuresWith(
	context: ProposalContext,
	start: FiguresDraft,
	proposal: FiguresProposal,
): FiguresResult {
	let draft = start;
	const grid = () => figuresGrid(context.facts, context.figures, draft, context.order);

	// A new row is added when a value is given for it, not before: a row
	// with nothing in it is not kept by the save, as an empty column is not.
	const addRow = (name: string) => {
		const row = proposal.rows.find((r) => sameName(r.label, name));
		if (!row) return;
		const used = draft.custom.map((f) => Number(/^custom_new_(\d+)$/.exec(f.key)?.[1] ?? 0));
		const key = `custom_new_${Math.max(0, ...used) + 1}`;
		draft = {
			...draft,
			custom: [...draft.custom, { key, label: row.label.slice(0, 120), unit: row.unit, periods: row.periods }],
		};
	};
	const addColumn = (name: string): string => {
		const found = findColumn(grid(), draft, name);
		if (found) return found;
		const added = withNewColumn(draft, grid().periods);
		draft = withColumnName(added.draft, added.key, name);
		return added.key;
	};

	const values: PlacedValue[] = proposal.values.map((entry) => {
		if (!findRow(grid(), draft, entry.figure)) addRow(entry.figure);
		const row = findRow(grid(), draft, entry.figure);
		const placed = (problem: string | null, label = entry.figure, column = entry.period ?? ""): PlacedValue => ({
			label,
			column,
			before: "—",
			after: row ? formatTableValue(entry.value, row.unit) : String(entry.value),
			source: entry.source,
			note: entry.note,
			problem,
		});
		if (!row) return placed("This is not a figure of the financial summary.");
		if (row.derived) {
			return placed(`${row.label} is calculated; it follows from the figures it is calculated from.`, row.label);
		}
		if (!row.point && !entry.period) return placed(`${row.label} needs a column to sit under.`, row.label);
		if (row.listed) draft = withRow(draft, row.key);
		const period = row.point ? null : addColumn(entry.period as string);
		const at = [...grid().series, ...grid().point]
			.find((r) => r.key === row.key)
			?.cells.find((cell) => samePeriod(cell.period, period));
		const fact = at?.fact ?? null;
		const before = fact && fact.value !== null ? formatTableValue(fact.value, row.unit) : "—";
		draft = withEntry(draft, {
			key: row.key,
			period,
			value: entry.value,
			note: entry.note,
			...(entry.source ? { source: entry.source } : {}),
		});
		return {
			...placed(null, row.label, period ? columnName(draft, period) : ""),
			before,
		};
	});
	// A column asked for with nothing under it is not added: an empty column
	// is not kept by the save, and would only read as one that was.
	return { draft, values };
}

function placeFigures(context: ProposalContext, proposal: FiguresProposal, draft: FiguresDraft): PlacedFigures {
	const { values } = figuresWith(context, draft, proposal);
	const good = values.filter((v) => v.problem === null);
	const title =
		context.sections.find((s) => s.key === "financial_summary")?.title ?? "Financial Summary";
	const columns = Array.from(new Set(good.map((v) => v.column).filter(Boolean)));
	const known = figuresGrid(context.facts, context.figures, draft, context.order).periods.map((p) =>
		columnName(draft, p),
	);
	const added = columns.filter((c) => !known.some((k) => samePeriod(k, c) || sameName(k, c)));
	return {
		kind: "figures",
		proposal,
		values,
		title,
		summary: [
			`${count(good.length, "value")}`,
			added.length > 0 ? `${count(added.length, "column")} added (${added.join(", ")})` : null,
		]
			.filter(Boolean)
			.join(", "),
		problem: good.length === 0 ? (values[0]?.problem ?? "None of these values can be put in.") : null,
		sources: distinct(good.map((v) => v.source)),
	};
}

const headerFact = (facts: DealScreeningFact[], name: string): DealScreeningFact | null =>
	facts.find((f) => f.kind === "header" && f.key === name) ??
	facts.find((f) => f.kind === "header" && sameName(f.label, name)) ??
	null;

/** A company fact's value as it is typed in its field. */
const typedHeader = (fact: DealScreeningFact, value: string | number): string =>
	typeof value === "number" && fact.unit !== "text" ? typedFigure(value, fact.unit) : String(value);

function placeFacts(context: ProposalContext, proposal: FactsProposal): PlacedFacts {
	const values = proposal.values.map((entry): PlacedValue => {
		const fact = headerFact(context.facts, entry.field);
		const base = { column: "", source: entry.source, note: entry.note };
		if (!fact) {
			return {
				...base,
				label: entry.field,
				before: "—",
				after: String(entry.value),
				problem: "This is not one of the memo's company facts.",
			};
		}
		const numeric = fact.unit !== "text";
		const value = numeric && typeof entry.value === "string" ? numberOf(entry.value) : entry.value;
		if (value === null) {
			return {
				...base,
				label: fact.label,
				before: headerText(fact) || "Not found",
				after: String(entry.value),
				problem: `${fact.label} is a number.`,
			};
		}
		return {
			...base,
			label: fact.label,
			before: headerText(fact) ? formatFactValue(fact.value, fact.unit) : "Not found",
			after: numeric && typeof value === "number" ? formatFactValue(value, fact.unit) : String(value),
			problem: null,
		};
	});
	const good = values.filter((v) => v.problem === null);
	return {
		kind: "company_facts",
		proposal,
		values,
		title: context.companyFactsTitle ?? "Company facts",
		summary: count(good.length, "field"),
		problem: good.length === 0 ? (values[0]?.problem ?? "None of these fields can be filled.") : null,
		sources: distinct(good.map((v) => v.source)),
	};
}

function placeSection(context: ProposalContext, proposal: SectionProposal): PlacedSection {
	const afterSection = proposal.after ? (context.sections.find((s) => s.key === proposal.after) ?? null) : null;
	const paragraphs = proposal.paragraphs.length;
	const base = {
		kind: "section" as const,
		proposal,
		afterSection,
		title: proposal.title,
		summary: `new section, ${count(paragraphs, "paragraph")}`,
		sources: distinct(proposal.paragraphs.flatMap((p) => p.sources.map((entry) => entry.source))),
	};
	if (proposal.after && !afterSection) {
		return { ...base, problem: "Could not find the section it goes after in the memo as it reads now." };
	}
	if (context.sections.some((s) => sameName(s.title, proposal.title))) {
		return { ...base, problem: `The memo already has a section named “${proposal.title}”.` };
	}
	return { ...base, problem: null };
}

/** The chart among a section's whose title the proposal names, or -1. */
export function matchChart(titles: (string | null)[], wanted: string): number {
	const exact = titles.findIndex((title) => title !== null && sameName(title, wanted));
	if (exact >= 0) return exact;
	return titles.findIndex((title) => title !== null && clean(title).toLowerCase().includes(clean(wanted).toLowerCase()));
}

const chartKey = (chart: DealScreeningChart): string =>
	JSON.stringify([chart.chart_type, clean(chart.title), chart.data]);

function placeChart(context: ProposalContext, proposal: ChartProposal): PlacedChart | null {
	const section = context.sections.find((s) => s.key === proposal.section);
	if (!section) return null;
	const charts = section.paragraphs.filter((p) => p.kind === "chart" && p.chart);
	const name = proposal.chart.title ? `the ${proposal.chart.title} chart` : "a chart";
	const base = {
		kind: "chart" as const,
		proposal,
		section,
		after: proposal.chart,
		title: section.title,
		sources: distinct(proposal.sources),
	};
	if (!context.isEditable(section)) {
		return { ...base, before: null, summary: name, problem: NOT_EDITABLE };
	}
	if (!proposal.replace) {
		return { ...base, before: null, summary: `${name} added`, problem: null };
	}
	const index = matchChart(
		charts.map((p) => p.chart?.title ?? null),
		proposal.replace,
	);
	if (index < 0) {
		return {
			...base,
			before: null,
			summary: name,
			problem: "Could not find this chart in the memo as it reads now.",
		};
	}
	const before = charts[index].chart ?? null;
	return {
		...base,
		before,
		summary: `${name} redrawn`,
		problem: before && chartKey(before) === chartKey(proposal.chart) ? "The chart already reads this way." : null,
	};
}

function placeTitle(context: ProposalContext, proposal: TitleProposal): PlacedTitle | null {
	const section = context.sections.find((s) => s.key === proposal.section);
	if (!section) return null;
	return {
		kind: "title",
		proposal,
		section,
		before: section.title,
		after: proposal.title,
		title: section.title,
		summary: "renamed",
		problem: sameName(section.title, proposal.title) ? "The section already has this title." : null,
		sources: [],
	};
}

/**
 * Any proposal against the memo on screen. `draft` is the financial summary
 * as the person has it in their unsaved edits, so a value is placed against
 * the columns and rows they already added.
 */
export function placeEdit(context: ProposalContext, edit: MemoEdit, draft: FiguresDraft): PlacedEdit | null {
	switch (edit.kind) {
		case "table":
			return placeTable(context, edit);
		case "figures":
			return placeFigures(context, edit, draft);
		case "company_facts":
			return placeFacts(context, edit);
		case "title":
			return placeTitle(context, edit);
		case "chart":
			return placeChart(context, edit);
		case "section":
			return placeSection(context, edit);
		default:
			return placeParagraph(context.sections, edit, context.isEditable);
	}
}

// ------------------------------------------------------------------ into the draft

/** A sentence as text, its words naming their document when they have one. */
const sentenceNode = (proposal: MemoEditProposal, text: string): JSONContent => {
	const source = proposal.sources?.find((s) => s.quote === text)?.source;
	return source ? { type: "text", text, marks: [sourceMark("", source)] } : { type: "text", text };
};

/** The proposal as the memo's bullets: a bold lead-in item, then one per sentence. */
const listNode = (proposal: MemoEditProposal, attrs: JSONContent["attrs"]): JSONContent => {
	const item = (content: JSONContent): JSONContent => ({
		type: "listItem",
		content: [{ type: "paragraph", content: [content] }],
	});
	return {
		type: "bulletList",
		attrs,
		content: [
			...(proposal.lead ? [item({ type: "text", text: proposal.lead, marks: [{ type: "leadIn" }] })] : []),
			...proposal.sentences.map((s) => item(sentenceNode(proposal, s))),
		],
	};
};

const paragraphNode = (proposal: MemoEditProposal, attrs: JSONContent["attrs"]): JSONContent => {
	const content: JSONContent[] = [];
	if (proposal.lead) {
		content.push({ type: "text", text: proposal.lead, marks: [{ type: "leadIn" }] }, { type: "text", text: " " });
	}
	proposal.sentences.forEach((sentence, index) => {
		if (index > 0) content.push({ type: "text", text: " " });
		content.push(sentenceNode(proposal, sentence));
	});
	return { type: "paragraph", attrs, content };
};

/** The blocks of a section's draft that hold text or a table, with their place. */
const nonEmptyBlocks = (section: DealScreeningSection, blocks: JSONContent[]) =>
	blocks
		.map((block, position) => ({ block, position }))
		.filter(({ block }) => itemsFromDoc(section, { type: "doc", content: [block] }).length > 0);

/**
 * The section's draft with the proposal in it: the matched paragraph
 * rewritten in place (it keeps its anchor, so the save is a replace) or
 * taken out, or a new paragraph at the end.
 */
export function applyProposalToDoc(
	section: DealScreeningSection,
	doc: JSONContent,
	proposal: MemoEditProposal,
): JSONContent {
	const blocks = [...(doc.content ?? [])];
	if (proposal.replace) {
		const index = matchParagraph(itemsFromDoc(section, doc), proposal.replace);
		// itemsFromDoc skips empty blocks; map back to the block it came from.
		const target = index >= 0 ? nonEmptyBlocks(section, blocks)[index] : undefined;
		if (target) {
			if (proposal.delete) {
				blocks.splice(target.position, 1);
				return { ...doc, content: blocks.length > 0 ? blocks : [{ type: "paragraph" }] };
			}
			const kept = { anchor: target.block.attrs?.anchor ?? null, baseId: target.block.attrs?.baseId ?? null };
			// A bulleted paragraph stays bulleted, like the rest of its section.
			blocks[target.position] =
				target.block.type === "bulletList" || target.block.type === "orderedList"
					? { ...listNode(proposal, kept), type: target.block.type }
					: paragraphNode(proposal, kept);
			return { ...doc, content: blocks };
		}
	}
	// A paragraph to take out that is not there is already out.
	if (proposal.delete) return doc;
	const added = paragraphNode(proposal, { anchor: null, baseId: null });
	// Applied twice, it is in once: the section already ends with it, or has it.
	const reads = (block: JSONContent) =>
		itemsFromDoc(section, { type: "doc", content: [block] })
			.map((item) => `${clean(item.lead)}\u0000${clean(item.text)}`)
			.join("\u0001");
	const wanted = reads(added);
	if (wanted !== "" && blocks.some((block) => block.type !== "memoTable" && reads(block) === wanted)) return doc;
	blocks.push(added);
	return { ...doc, content: blocks };
}

/** Two tables that read alike: caption, columns and every cell. */
const sameTable = (a: EditTable, b: EditTable): boolean =>
	clean(a.caption) === clean(b.caption) &&
	a.columns.length === b.columns.length &&
	a.columns.every((column, c) => clean(column) === clean(b.columns[c])) &&
	a.rows.length === b.rows.length &&
	changedCells(a, b).size === 0;

/**
 * A proposed table over the one it replaces: a cell left as it reads keeps
 * the note and the document the person's draft had on it, since the chat
 * gives a source only for what it changes.
 */
export function mergedTable(before: EditTable | null, after: EditTable): EditTable {
	if (!before) return after;
	const notes: Record<string, string> = {};
	const sources: Record<string, DocumentSource> = { ...(after.sources ?? {}) };
	after.rows.forEach((row, r) =>
		row.forEach((text, c) => {
			const key = cellKey(r, c);
			if (clean(before.rows[r]?.[c]) !== clean(text)) return;
			if (before.notes?.[key]) notes[key] = before.notes[key];
			if (before.sources?.[key] && !sources[key]) sources[key] = before.sources[key];
		}),
	);
	const { notes: _notes, sources: _sources, ...rest } = after;
	return {
		...rest,
		...(Object.keys(notes).length > 0 ? { notes } : {}),
		...(Object.keys(sources).length > 0 ? { sources } : {}),
	};
}

const tableOf = (block: JSONContent): EditTable | null => (block.attrs?.table as EditTable | null | undefined) ?? null;

/**
 * The section's draft with the proposed table in it: over the table its
 * caption names, which keeps its anchor so the save is a replace, or as a
 * new table at the end.
 */
export function applyTableToDoc(doc: JSONContent, proposal: TableProposal): JSONContent {
	const blocks = [...(doc.content ?? [])];
	const tables = blocks
		.map((block, position) => ({ block, position }))
		.filter(({ block }) => block.type === "memoTable" && tableOf(block));
	if (proposal.replace) {
		const index = matchTable(
			tables.map(({ block }) => tableOf(block)?.caption ?? (block.attrs?.caption as string | null) ?? null),
			proposal.replace,
		);
		const target = index >= 0 ? tables[index] : undefined;
		if (target) {
			const table = mergedTable(tableOf(target.block), proposal.table);
			blocks[target.position] = {
				...target.block,
				attrs: { ...target.block.attrs, caption: table.caption ?? target.block.attrs?.caption ?? "Table", table },
			};
			return { ...doc, content: blocks };
		}
	}
	// Applied twice, it is in once.
	if (tables.some(({ block }) => sameTable(tableOf(block) as EditTable, proposal.table))) return doc;
	blocks.push({
		type: "memoTable",
		attrs: {
			anchor: null,
			caption: proposal.table.caption ?? "Table",
			table: proposal.table,
			// Nothing was there when editing started: every cell is a change.
			startTable: null,
		},
	});
	return { ...doc, content: blocks };
}

const chartOf = (block: JSONContent): DealScreeningChart | null =>
	(block.attrs?.chart as DealScreeningChart | null | undefined) ?? null;

/**
 * The section's draft with the proposed chart in it (DOP-655): over the one
 * its title names, or as a new chart at the end. Applied twice, it is in
 * once. The chart carries the documents its values were read from.
 */
export function applyChartToDoc(doc: JSONContent, proposal: ChartProposal): JSONContent {
	const blocks = [...(doc.content ?? [])];
	const chart: DealScreeningChart = {
		...proposal.chart,
		...(proposal.sources.length > 0 ? { sources: proposal.sources.map(chartSource) } : {}),
	};
	const charts = blocks
		.map((block, position) => ({ block, position }))
		.filter(({ block }) => block.type === "memoChart" && chartOf(block));
	if (proposal.replace) {
		const index = matchChart(
			charts.map(({ block }) => chartOf(block)?.title ?? null),
			proposal.replace,
		);
		const target = index >= 0 ? charts[index] : undefined;
		if (target) {
			blocks[target.position] = { ...target.block, attrs: { ...target.block.attrs, chart } };
			return { ...doc, content: blocks };
		}
	}
	if (charts.some(({ block }) => chartKey(chartOf(block) as DealScreeningChart) === chartKey(chart))) return doc;
	blocks.push({ type: "memoChart", attrs: { anchor: null, chart } });
	return { ...doc, content: blocks };
}

/** A document a proposal names, as the memo's chart carries it until it is saved and read again. */
const chartSource = (source: DocumentSource): DealScreeningSource => ({
	attachment_id: source.attachment_id,
	filename: source.filename ?? "",
	doc_type: "",
	quote: source.quote ?? "",
	anchor: "",
	verified: false,
	page: source.page ?? null,
	sheet: source.sheet ?? null,
	cell: source.cell ?? null,
	ocr: false,
	kind: "document",
});

/**
 * The whole memo's draft with a new section in it (DOP-656): its heading,
 * a person's own, after the blocks of the section it follows (or last), and
 * its paragraphs as the person's additions. Applied twice, it is in once.
 */
export function applySectionToDoc(doc: JSONContent, placed: PlacedSection): JSONContent {
	const blocks = [...(doc.content ?? [])];
	const headings = blocks
		.map((block, position) => ({ block, position }))
		.filter(({ block }) => block.type === "memoSectionHeading");
	if (headings.some(({ block }) => sameName(String(block.attrs?.title ?? ""), placed.proposal.title))) return doc;
	let at = blocks.length;
	if (placed.afterSection) {
		const index = headings.findIndex(({ block }) => block.attrs?.key === placed.afterSection?.key);
		if (index >= 0) at = index + 1 < headings.length ? headings[index + 1].position : blocks.length;
	}
	const heading: JSONContent = {
		type: "memoSectionHeading",
		attrs: { key: `new-${Math.random().toString(36).slice(2, 10)}`, title: placed.proposal.title, startTitle: "", editable: true, user: true },
	};
	const paragraphs = placed.proposal.paragraphs.map((paragraph) =>
		paragraphNode(
			{ section: "", replace: null, lead: paragraph.lead, sentences: paragraph.sentences, sources: paragraph.sources },
			{ anchor: null, baseId: null },
		),
	);
	blocks.splice(at, 0, heading, ...paragraphs);
	return { ...doc, content: blocks };
}

/** The whole memo's draft with a section's title as proposed. */
export function applyTitleToDoc(doc: JSONContent, proposal: TitleProposal): JSONContent {
	return {
		...doc,
		content: (doc.content ?? []).map((node) =>
			node.type === "memoSectionHeading" && node.attrs?.key === proposal.section
				? { ...node, attrs: { ...node.attrs, title: proposal.title } }
				: node,
		),
	};
}

/** The company facts' draft with the proposed values in it. */
export function factsWith(context: ProposalContext, start: FiguresDraft, proposal: FactsProposal): FiguresDraft {
	let draft = start;
	proposal.values.forEach((entry) => {
		const fact = headerFact(context.facts, entry.field);
		if (!fact) return;
		const value =
			fact.unit !== "text" && typeof entry.value === "string" ? numberOf(entry.value) : entry.value;
		if (value === null) return;
		draft = withHeader(draft, fact, typedHeader(fact, value), entry.note, entry.source);
	});
	return draft;
}

/** The edit draft: the memo's text as one document, and the figures beside it. */
export interface EditDraft {
	doc: JSONContent;
	figures: FiguresDraft;
}

/**
 * The draft with a placed proposal in it. `sectionPart` and `withSection`
 * cut a section's blocks out of the whole memo and put them back
 * (`splitMemoDoc`, `withSectionDoc`), so this file does not import the hook
 * that owns the draft.
 */
export function applyEdit(
	context: ProposalContext,
	draft: EditDraft,
	placed: PlacedEdit,
	sectionPart: (doc: JSONContent, key: string) => JSONContent | undefined,
	withSection: (doc: JSONContent, key: string, part: JSONContent) => JSONContent,
): EditDraft {
	if (placed.problem) return draft;
	switch (placed.kind) {
		case "figures":
			return { ...draft, figures: figuresWith(context, draft.figures, placed.proposal).draft };
		case "company_facts":
			return { ...draft, figures: factsWith(context, draft.figures, placed.proposal) };
		case "title":
			return { ...draft, doc: applyTitleToDoc(draft.doc, placed.proposal) };
		case "section":
			return { ...draft, doc: applySectionToDoc(draft.doc, placed) };
		case "chart": {
			const part = sectionPart(draft.doc, placed.section.key);
			return part
				? { ...draft, doc: withSection(draft.doc, placed.section.key, applyChartToDoc(part, placed.proposal)) }
				: draft;
		}
		case "table": {
			const part = sectionPart(draft.doc, placed.section.key);
			return part
				? { ...draft, doc: withSection(draft.doc, placed.section.key, applyTableToDoc(part, placed.proposal)) }
				: draft;
		}
		default: {
			const part = sectionPart(draft.doc, placed.section.key);
			return part
				? {
						...draft,
						doc: withSection(
							draft.doc,
							placed.section.key,
							applyProposalToDoc(placed.section, part, placed.proposal),
						),
					}
				: draft;
		}
	}
}
